import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Login as LoginIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface AuthPromptProps {
  open: boolean;
  onClose: () => void;
  action: string; // e.g., "like this outfit", "follow this user", "upload outfits"
  title?: string;
}

/**
 * Authentication prompt component for anonymous users
 * Shows when users try to perform actions that require authentication
 */
const AuthPrompt: React.FC<AuthPromptProps> = ({
  open,
  onClose,
  action,
  title = "Sign In Required"
}) => {
  const navigate = useNavigate();

  const handleLogin = () => {
    onClose();
    navigate('/login');
  };

  const handleContinueBrowsing = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <LoginIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          
          <Typography variant="body1" gutterBottom>
            You need to be signed in to {action}.
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Don't worry - you can continue browsing outfits without signing in!
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleLogin}
          startIcon={<LoginIcon />}
          size="large"
        >
          Sign In
        </Button>
        
        <Button
          variant="outlined"
          onClick={handleContinueBrowsing}
          size="large"
        >
          Continue Browsing
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuthPrompt;